import { APP_BASE_HREF } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import * as FeatureCookieConsent from '@planone/shared/feature-cookie-consent';
import {
  GOOGLE_ANALYTICS_CONFIGURATION,
  GoogleAnalyticsConfiguration,
  GoogleAnalyticsService,
} from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import {
  SNOWPLOW_CONFIGURATION,
  SnowplowConfiguration,
  SnowplowService,
} from '@planone/shared/feature-tracking-snowplow';
import { DocumentRef } from '@planone/shared/util-browser-api';
import { REQUEST } from '@planone/shared/util-ssr';
import { setCustomUrl, setReferrerUrl } from '@snowplow/browser-tracker';
import { defer, filter, of, pairwise, startWith, switchMap, tap, withLatestFrom } from 'rxjs';

import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class GeneralEffects {
  initialize$ = createEffect(
    () =>
      defer(() => {
        this.store.dispatch(FeatureTrackingGoogleAnalytics.initialize(this.googleAnalyticsConfiguration));
        this.store.dispatch(FeatureTrackingSnowplow.initialize(this.snowplowConfiguration));

        return of(true);
      }),
    { dispatch: false }
  );

  cookieConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureCookieConsent.initialize),
      filter((action) => action.status === undefined),
      switchMap(() =>
        of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'pageview_consent',
          })
        )
      )
    )
  );

  routing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        startWith(undefined),
        pairwise(),
        withLatestFrom(this.authenticationFacade.excludeAnalytics$),
        withLatestFrom(this.fairsFacade.fairId$),
        tap(([[actions, exclude_analytics], fair_id]) => {
          const previousRoute = (<any>actions[0])?.payload.routerState;
          const currentRoute = (<any>actions[1])?.payload.routerState;
          const data = this.googleAnalyticsService.extractData(currentRoute);
          const currentUrlObject = new URL(this.documentRef.nativeDocument.URL ?? this.request.url);

          const pageType = data?.analyticsPageType ?? this.googleAnalyticsService.getPageType(currentRoute.url);

          if (previousRoute) {
            setReferrerUrl(
              this.snowplowService.excludeSearchParams(
                currentUrlObject.origin + this.baseHref.slice(0, -1) + previousRoute.url
              )
            );
          }

          setCustomUrl(this.snowplowService.excludeSearchParams(this.documentRef.nativeDocument.URL));

          if (currentRoute && !data?.snowplow?.customTracking) {
            this.store.dispatch(
              FeatureTrackingSnowplow.pageView({
                title: this.title.getTitle(),
                context: [
                  {
                    schema: CustomEventIglu.page_view,
                    data: {
                      context: fair_id,
                      page_type: pageType,
                      exclude_analytics: exclude_analytics,
                    },
                  },
                ],
              })
            );
          }

          if (currentRoute && !data?.googleAnalytics?.customTracking) {
            this.store.dispatch(
              FeatureTrackingGoogleAnalytics.push({
                event: 'pageview',
                context: fair_id,
                page_type: pageType,
                exclude_analytics: exclude_analytics,
              })
            );
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(APP_BASE_HREF) private baseHref: string,
    @Inject(SNOWPLOW_CONFIGURATION) private snowplowConfiguration: SnowplowConfiguration,
    @Inject(GOOGLE_ANALYTICS_CONFIGURATION) private googleAnalyticsConfiguration: GoogleAnalyticsConfiguration,
    @Optional() @Inject(REQUEST) private request: Request,
    private readonly actions$: Actions,
    private store: Store,
    private title: Title,
    private fairsFacade: DataAccessFairsFacade,
    private authenticationFacade: AuthenticationFacade,
    private googleAnalyticsService: GoogleAnalyticsService,
    private snowplowService: SnowplowService,
    private documentRef: DocumentRef
  ) {}
}
